import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { checkoutService } from '../services/checkoutService';
import Swal from 'sweetalert2';

const Checkout = () => {
    const navigate = useNavigate();
    const { cartItems, getTotalItems, getTotalPrice, clearCart } = useCart();

    // Estados para os dados do checkout
    const [formData, setFormData] = useState({
        // Seção 1 - Dados pessoais
        nome: '',
        sobrenome: '',
        email: '',
        telefone: '',

        // Seção 2 - Endereço e entrega
        cep: '',
        endereco: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
        formaEntrega: '',

        // Seção 3 - Pagamento
        formaPagamento: ''
    });

    const [shippingRates, setShippingRates] = useState([]);
    const [loadingShipping, setLoadingShipping] = useState(false);
    const [shippingError, setShippingError] = useState('');
    const [selectedShipping, setSelectedShipping] = useState(null);
    const [submittingOrder, setSubmittingOrder] = useState(false);

    // Configurações de envio
    const shippingConfig = {
        originCep: '81670-130',
        productWeight: 0.2, // 200g por unidade de produto
        productDimensions: {
            length: 20,
            width: 15,
            height: 10
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const formatCep = (value) => {
        const numbers = value.replace(/\D/g, '');
        const limitedNumbers = numbers.slice(0, 8);
        if (limitedNumbers.length <= 5) {
            return limitedNumbers;
        } else {
            return `${limitedNumbers.slice(0, 5)}-${limitedNumbers.slice(5)}`;
        }
    };

    const formatPhone = (value) => {
        const numbers = value.replace(/\D/g, '');
        const limitedNumbers = numbers.slice(0, 11);
        if (limitedNumbers.length <= 2) {
            return limitedNumbers;
        } else if (limitedNumbers.length <= 7) {
            return `(${limitedNumbers.slice(0, 2)}) ${limitedNumbers.slice(2)}`;
        } else {
            return `(${limitedNumbers.slice(0, 2)}) ${limitedNumbers.slice(2, 7)}-${limitedNumbers.slice(7)}`;
        }
    };

    const handlePhoneChange = (value) => {
        const formattedPhone = formatPhone(value);
        setFormData(prev => ({ ...prev, telefone: formattedPhone }));
    };

    const handleCepChange = async (value) => {
        const formattedCep = formatCep(value);
        setFormData(prev => ({ ...prev, cep: formattedCep }));

        // Se o CEP tem 8 dígitos, buscar automaticamente
        const cleanCep = formattedCep.replace(/\D/g, '');
        if (cleanCep.length === 8) {
            // Usar o CEP limpo diretamente ao invés de depender do estado
            await fetchAddressAndShipping(cleanCep);
        }
    };

    const fetchAddressAndShipping = async (cleanCep) => {
        setLoadingShipping(true);
        setShippingError('');
        setShippingRates([]);

        try {
            // Buscar endereço pelo CEP usando ViaCEP
            const cepResponse = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
            const cepData = await cepResponse.json();

            if (cepData.erro) {
                setShippingError('CEP não encontrado. Verifique o número digitado.');
                return;
            }

            // Preencher automaticamente os campos do endereço
            setFormData(prev => ({
                ...prev,
                endereco: cepData.logradouro || '',
                bairro: cepData.bairro || '',
                cidade: cepData.localidade || '',
                estado: cepData.uf || ''
            }));

            // Calcular peso total do carrinho em gramas (200g por unidade de produto × quantidade)
            const pesoTotalGrams = cartItems.reduce((total, item) => {
                const pesoItemKg = shippingConfig.productWeight || 0.2; // 200g (0.2kg) por produto
                const pesoItemGrams = pesoItemKg * 1000; // Converter para gramas
                return total + (pesoItemGrams * item.quantity);
            }, 0);

            // Buscar dados de frete da API (peso em gramas)
            const shippingData = await checkoutService.calculateShipping(cleanCep, pesoTotalGrams);

            // Mapear a resposta do backend para o formato esperado pelo frontend
            const mappedShippingData = shippingData.map(item => ({
                service: item.servico,
                name: item.servico,
                price: item.preco_frete,
                deliveryTime: `${item.prazo_minimo}-${item.prazo_maximo} dias úteis após a postagem`
            }));

            setShippingRates(mappedShippingData);

            // Colocar foco no campo número após preenchimento
            setTimeout(() => {
                const numeroField = document.querySelector('input[name="numero"]');
                if (numeroField) {
                    numeroField.focus();
                }
            }, 100);

        } catch (err) {
            console.error('Erro ao calcular frete:', err);
            setShippingError('Erro ao calcular frete. Verifique o CEP e tente novamente.');
        } finally {
            setLoadingShipping(false);
        }
    };

    const calculateShipping = async () => {
        const cleanCep = formData.cep.replace(/\D/g, '');
        if (!cleanCep || cleanCep.length !== 8) {
            setShippingError('Digite um CEP válido (8 dígitos)');
            return;
        }

        await fetchAddressAndShipping(cleanCep);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validações básicas
        if (!formData.nome || !formData.sobrenome || !formData.email) {
            Swal.fire({
                icon: 'warning',
                title: 'Campos obrigatórios',
                text: 'Por favor, preencha todos os campos obrigatórios da seção 1.'
            });
            return;
        }

        if (!formData.cep || !formData.endereco || !formData.numero || !formData.bairro || !formData.cidade || !formData.estado) {
            Swal.fire({
                icon: 'warning',
                title: 'Endereço incompleto',
                text: 'Por favor, preencha todos os campos obrigatórios do endereço.'
            });
            return;
        }

        if (!selectedShipping) {
            Swal.fire({
                icon: 'warning',
                title: 'Frete não selecionado',
                text: 'Por favor, selecione uma forma de entrega.'
            });
            return;
        }

        if (!formData.formaPagamento) {
            Swal.fire({
                icon: 'warning',
                title: 'Pagamento não selecionado',
                text: 'Por favor, selecione uma forma de pagamento.'
            });
            return;
        }

        // Montar payload no formato esperado
        const payload = {
            customer: {
                name: `${formData.nome} ${formData.sobrenome}`,
                email: formData.email,
                phone: formData.telefone || '',
                address: formData.endereco,
                address_number: formData.numero,
                address_complement: formData.complemento || '',
                zip_code: formData.cep.replace(/\D/g, ''),
                city: formData.cidade,
                neighborhood: formData.bairro,
                state: formData.estado
            },
            products: cartItems.map(item => ({
                id: item.id,
                quantity: item.quantity,
                base_price: Math.round(item.currentPrice * 100) // Converter para centavos
            })),
            shipping_method: {
                name: selectedShipping.name,
                delivery_days: parseInt(selectedShipping.deliveryTime.split('-')[1]?.split(' ')[0] || selectedShipping.deliveryTime.split('-')[0]),
                cost: Math.round(selectedShipping.price * 100) // Converter para centavos
            },
            sales_channel: {
                name: 'Online'
            },
            payment_methods: [
                {
                    name: formData.formaPagamento === 'pix' ? 'PIX' : 'Cartão de Crédito',
                    amount: Math.round((getTotalPrice() + selectedShipping.price) * 100), // Converter para centavos
                    is_paid: false
                }
            ],
            amount: Math.round((getTotalPrice() + selectedShipping.price) * 100), // Converter para centavos
            payment_received: false
        };

        // Exibir payload no console
        console.log('Checkout Payload:', JSON.stringify(payload, null, 2));

        // Fazer chamada POST para a API
        try {
            setSubmittingOrder(true);
            
            const response = await checkoutService.createPublicOrder(payload);
            
            console.log('Pedido criado com sucesso:', response.data);
            
            // Limpar carrinho antes de navegar
            clearCart();
            
            // Navegar para página de confirmação com os dados do pedido
            navigate('/pedido/confirmacao', {
                state: {
                    order: response.data.order
                }
            });
        } catch (error) {
            console.error('Erro ao criar pedido:', error);
            
            let errorMessage = 'Erro ao processar seu pedido. Tente novamente.';
            if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            Swal.fire({
                icon: 'error',
                title: 'Erro ao criar pedido',
                text: errorMessage,
                confirmButtonText: 'OK'
            });
        } finally {
            setSubmittingOrder(false);
        }
    };

    if (cartItems.length === 0) {
        return (
            <section className="wrapper style1">
                <div className="container">
                    <div style={{ textAlign: 'center', padding: '50px 0' }}>
                        <h2>Seu carrinho está vazio</h2>
                        <p>Adicione alguns produtos antes de finalizar a compra.</p>
                        <button onClick={() => navigate('/')} style={{
                            padding: '15px 30px',
                            backgroundColor: '#edf507',
                            color: '#000000',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            cursor: 'pointer'
                        }}>
                            Continuar Comprando
                        </button>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="wrapper style1">
            <div className="container">
                <h1 style={{
                    fontSize: '2.5rem',
                    fontWeight: 'bold',
                    color: '#000000',
                    margin: '0 0 30px 0',
                    textAlign: 'center'
                }}>
                    Finalizar Compra
                </h1>

                <form onSubmit={handleSubmit}>
                    <div className="row gtr-200">
                        {/* Formulário */}
                        <div className="col-8 col-12-narrower">
                            {/* Seção 1 - Dados Pessoais */}
                            <div style={{
                                backgroundColor: '#ffffff',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                padding: '25px',
                                margin: '0 0 25px 0'
                            }}>
                                <h2 style={{
                                    fontSize: '1.5rem',
                                    fontWeight: 'bold',
                                    color: '#000000',
                                    margin: '0 0 20px 0',
                                    paddingBottom: '10px',
                                    borderBottom: '2px solid #edf507'
                                }}>
                                    1. Dados Pessoais
                                </h2>

                                <div className="row gtr-50">
                                    <div className="col-6 col-12-narrower">
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.9rem',
                                            fontWeight: 'bold',
                                            color: '#000000',
                                            marginBottom: '5px'
                                        }}>
                                            Nome *
                                        </label>
                                        <input
                                            type="text"
                                            name="nome"
                                            value={formData.nome}
                                            onChange={handleInputChange}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: '2px solid #e0e0e0',
                                                borderRadius: '6px',
                                                fontSize: '1rem',
                                                outline: 'none'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#edf507'}
                                            onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                        />
                                    </div>
                                    <div className="col-6 col-12-narrower">
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.9rem',
                                            fontWeight: 'bold',
                                            color: '#000000',
                                            marginBottom: '5px'
                                        }}>
                                            Sobrenome *
                                        </label>
                                        <input
                                            type="text"
                                            name="sobrenome"
                                            value={formData.sobrenome}
                                            onChange={handleInputChange}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: '2px solid #e0e0e0',
                                                borderRadius: '6px',
                                                fontSize: '1rem',
                                                outline: 'none'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#edf507'}
                                            onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                        />
                                    </div>
                                </div>

                                <div className="row gtr-50" style={{ marginTop: '15px' }}>
                                    <div className="col-6 col-12-narrower">
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.9rem',
                                            fontWeight: 'bold',
                                            color: '#000000',
                                            marginBottom: '5px'
                                        }}>
                                            Email *
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: '2px solid #e0e0e0',
                                                borderRadius: '6px',
                                                fontSize: '1rem',
                                                outline: 'none'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#edf507'}
                                            onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                        />
                                    </div>
                                    <div className="col-6 col-12-narrower">
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.9rem',
                                            fontWeight: 'bold',
                                            color: '#000000',
                                            marginBottom: '5px'
                                        }}>
                                            Telefone
                                        </label>
                                        <input
                                            type="tel"
                                            name="telefone"
                                            value={formData.telefone}
                                            onChange={(e) => handlePhoneChange(e.target.value)}
                                            inputMode="numeric"
                                            placeholder="(00) 00000-0000"
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: '2px solid #e0e0e0',
                                                borderRadius: '6px',
                                                fontSize: '1rem',
                                                outline: 'none'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#edf507'}
                                            onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Seção 2 - Endereço e Entrega */}
                            <div style={{
                                backgroundColor: '#ffffff',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                padding: '25px',
                                margin: '0 0 25px 0'
                            }}>
                                <h2 style={{
                                    fontSize: '1.5rem',
                                    fontWeight: 'bold',
                                    color: '#000000',
                                    margin: '0 0 20px 0',
                                    paddingBottom: '10px',
                                    borderBottom: '2px solid #edf507'
                                }}>
                                    2. Endereço e Entrega
                                </h2>

                                {/* CEP */}
                                <div style={{ marginBottom: '15px' }}>
                                    <label style={{
                                        display: 'block',
                                        fontSize: '0.9rem',
                                        fontWeight: 'bold',
                                        color: '#000000',
                                        marginBottom: '5px'
                                    }}>
                                        CEP *
                                    </label>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <input
                                            type="text"
                                            name="cep"
                                            value={formData.cep}
                                            onChange={(e) => handleCepChange(e.target.value)}
                                            required
                                            placeholder="00000-000"
                                            style={{
                                                flex: '1',
                                                padding: '12px',
                                                border: '2px solid #e0e0e0',
                                                borderRadius: '6px',
                                                fontSize: '1rem',
                                                outline: 'none'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#edf507'}
                                            onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                        />
                                        <button
                                            type="button"
                                            onClick={calculateShipping}
                                            disabled={loadingShipping}
                                            style={{
                                                padding: '12px 20px',
                                                backgroundColor: loadingShipping ? '#ccc' : '#f8f8f8',
                                                color: '#000000',
                                                border: '2px solid #e0e0e0',
                                                borderRadius: '6px',
                                                fontSize: '1rem',
                                                fontWeight: 'bold',
                                                cursor: loadingShipping ? 'not-allowed' : 'pointer',
                                                transition: 'all 0.3s ease'
                                            }}
                                        >
                                            {loadingShipping ? 'Calculando...' : 'Calcular Frete'}
                                        </button>
                                    </div>
                                </div>

                                {/* Campos do endereço */}
                                <div className="row gtr-50">
                                    <div className="col-8 col-12-narrower">
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.9rem',
                                            fontWeight: 'bold',
                                            color: '#000000',
                                            marginBottom: '5px'
                                        }}>
                                            Endereço *
                                        </label>
                                        <input
                                            type="text"
                                            name="endereco"
                                            value={formData.endereco}
                                            onChange={handleInputChange}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: '2px solid #e0e0e0',
                                                borderRadius: '6px',
                                                fontSize: '1rem',
                                                outline: 'none'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#edf507'}
                                            onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                        />
                                    </div>
                                    <div className="col-4 col-12-narrower">
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.9rem',
                                            fontWeight: 'bold',
                                            color: '#000000',
                                            marginBottom: '5px'
                                        }}>
                                            Número *
                                        </label>
                                        <input
                                            type="text"
                                            name="numero"
                                            value={formData.numero}
                                            onChange={handleInputChange}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: '2px solid #e0e0e0',
                                                borderRadius: '6px',
                                                fontSize: '1rem',
                                                outline: 'none'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#edf507'}
                                            onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                        />
                                    </div>
                                </div>

                                <div className="row gtr-50" style={{ marginTop: '15px' }}>
                                    <div className="col-6 col-12-narrower">
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.9rem',
                                            fontWeight: 'bold',
                                            color: '#000000',
                                            marginBottom: '5px'
                                        }}>
                                            Complemento
                                        </label>
                                        <input
                                            type="text"
                                            name="complemento"
                                            value={formData.complemento}
                                            onChange={handleInputChange}
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: '2px solid #e0e0e0',
                                                borderRadius: '6px',
                                                fontSize: '1rem',
                                                outline: 'none'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#edf507'}
                                            onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                        />
                                    </div>
                                    <div className="col-6 col-12-narrower">
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.9rem',
                                            fontWeight: 'bold',
                                            color: '#000000',
                                            marginBottom: '5px'
                                        }}>
                                            Bairro *
                                        </label>
                                        <input
                                            type="text"
                                            name="bairro"
                                            value={formData.bairro}
                                            onChange={handleInputChange}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: '2px solid #e0e0e0',
                                                borderRadius: '6px',
                                                fontSize: '1rem',
                                                outline: 'none'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#edf507'}
                                            onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                        />
                                    </div>
                                </div>

                                <div className="row gtr-50" style={{ marginTop: '15px' }}>
                                    <div className="col-8 col-12-narrower">
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.9rem',
                                            fontWeight: 'bold',
                                            color: '#000000',
                                            marginBottom: '5px'
                                        }}>
                                            Cidade *
                                        </label>
                                        <input
                                            type="text"
                                            name="cidade"
                                            value={formData.cidade}
                                            onChange={handleInputChange}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: '2px solid #e0e0e0',
                                                borderRadius: '6px',
                                                fontSize: '1rem',
                                                outline: 'none'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#edf507'}
                                            onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                        />
                                    </div>
                                    <div className="col-4 col-12-narrower">
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.9rem',
                                            fontWeight: 'bold',
                                            color: '#000000',
                                            marginBottom: '5px'
                                        }}>
                                            Estado *
                                        </label>
                                        <select
                                            name="estado"
                                            value={formData.estado}
                                            onChange={handleInputChange}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: '2px solid #e0e0e0',
                                                borderRadius: '6px',
                                                fontSize: '1rem',
                                                outline: 'none'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#edf507'}
                                            onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                        >
                                            <option value="">Selecione</option>
                                            <option value="AC">Acre</option>
                                            <option value="AL">Alagoas</option>
                                            <option value="AP">Amapá</option>
                                            <option value="AM">Amazonas</option>
                                            <option value="BA">Bahia</option>
                                            <option value="CE">Ceará</option>
                                            <option value="DF">Distrito Federal</option>
                                            <option value="ES">Espírito Santo</option>
                                            <option value="GO">Goiás</option>
                                            <option value="MA">Maranhão</option>
                                            <option value="MT">Mato Grosso</option>
                                            <option value="MS">Mato Grosso do Sul</option>
                                            <option value="MG">Minas Gerais</option>
                                            <option value="PA">Pará</option>
                                            <option value="PB">Paraíba</option>
                                            <option value="PR">Paraná</option>
                                            <option value="PE">Pernambuco</option>
                                            <option value="PI">Piauí</option>
                                            <option value="RJ">Rio de Janeiro</option>
                                            <option value="RN">Rio Grande do Norte</option>
                                            <option value="RS">Rio Grande do Sul</option>
                                            <option value="RO">Rondônia</option>
                                            <option value="RR">Roraima</option>
                                            <option value="SC">Santa Catarina</option>
                                            <option value="SP">São Paulo</option>
                                            <option value="SE">Sergipe</option>
                                            <option value="TO">Tocantins</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Opções de frete */}
                                {shippingRates.length > 0 && (
                                    <div style={{ marginTop: '20px' }}>
                                        <h3 style={{
                                            fontSize: '1.2rem',
                                            fontWeight: 'bold',
                                            color: '#000000',
                                            margin: '0 0 15px 0'
                                        }}>
                                            Opções de Entrega:
                                        </h3>
                                        {shippingRates.map((rate) => (
                                            <label key={rate.service} style={{
                                                display: 'block',
                                                marginBottom: '10px',
                                                padding: '15px',
                                                border: selectedShipping?.service === rate.service ? '2px solid #000000' : '2px solid #e0e0e0',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                backgroundColor: selectedShipping?.service === rate.service ? '#fffbf0' : '#ffffff'
                                            }}>
                                                <input
                                                    type="radio"
                                                    name="formaEntrega"
                                                    value={rate.service}
                                                    checked={selectedShipping?.service === rate.service}
                                                    onChange={() => setSelectedShipping(rate)}
                                                    style={{ marginRight: '10px' }}
                                                />
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <div>
                                                        <strong style={{ color: selectedShipping?.service === rate.service ? '#000000' : '#000000' }}>{rate.name}</strong>
                                                        <div style={{ fontSize: '0.9rem', color: '#666' }}>{rate.deliveryTime}</div>
                                                    </div>
                                                    <div style={{
                                                        fontWeight: 'bold',
                                                        color: selectedShipping?.service === rate.service ? '#000000' : '#000000',
                                                        fontSize: selectedShipping?.service === rate.service ? '1.1rem' : '1rem'
                                                    }}>
                                                        R$ {rate.price.toFixed(2).replace('.', ',')}
                                                    </div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                )}

                                {shippingError && (
                                    <div style={{
                                        color: '#d32f2f',
                                        fontSize: '0.9rem',
                                        marginTop: '10px',
                                        padding: '8px',
                                        backgroundColor: '#ffebee',
                                        borderRadius: '4px',
                                        border: '1px solid #ffcdd2'
                                    }}>
                                        {shippingError}
                                    </div>
                                )}
                            </div>

                            {/* Seção 3 - Forma de Pagamento */}
                            <div style={{
                                backgroundColor: '#ffffff',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                padding: '25px',
                                margin: '0 0 25px 0'
                            }}>
                                <h2 style={{
                                    fontSize: '1.5rem',
                                    fontWeight: 'bold',
                                    color: '#000000',
                                    margin: '0 0 20px 0',
                                    paddingBottom: '10px',
                                    borderBottom: '2px solid #edf507'
                                }}>
                                    3. Forma de Pagamento
                                </h2>

                                <div style={{ display: 'flex', gap: '20px' }}>
                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '15px',
                                        border: formData.formaPagamento === 'pix' ? '2px solid #000000' : '2px solid #e0e0e0',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        flex: '1',
                                        transition: 'all 0.3s ease',
                                        backgroundColor: formData.formaPagamento === 'pix' ? '#fffbf0' : '#ffffff'
                                    }}>
                                        <input
                                            type="radio"
                                            name="formaPagamento"
                                            value="pix"
                                            checked={formData.formaPagamento === 'pix'}
                                            onChange={handleInputChange}
                                            style={{ marginRight: '10px' }}
                                        />
                                        <div>
                                            <strong style={{
                                                fontSize: formData.formaPagamento === 'pix' ? '1.1rem' : '1rem',
                                                color: '#000000'
                                            }}>
                                                PIX
                                            </strong>
                                            <div style={{ fontSize: '0.9rem', color: '#666', lineHeight: '1.3' }}>
                                                Você será redirecionado ao Mercado Pago onde um QR Code será gerado para pagamento instantâneo e seguro
                                            </div>
                                        </div>
                                    </label>

                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '15px',
                                        border: formData.formaPagamento === 'credito' ? '2px solid #000000' : '2px solid #e0e0e0',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        flex: '1',
                                        transition: 'all 0.3s ease',
                                        backgroundColor: formData.formaPagamento === 'credito' ? '#fffbf0' : '#ffffff'
                                    }}>
                                        <input
                                            type="radio"
                                            name="formaPagamento"
                                            value="credito"
                                            checked={formData.formaPagamento === 'credito'}
                                            onChange={handleInputChange}
                                            style={{ marginRight: '10px' }}
                                        />
                                        <div>
                                            <strong style={{
                                                fontSize: formData.formaPagamento === 'credito' ? '1.1rem' : '1rem',
                                                color: '#000000'
                                            }}>
                                                Cartão de Crédito
                                            </strong>
                                            <div style={{ fontSize: '0.9rem', color: '#666', lineHeight: '1.3' }}>
                                                Após ser direcionado ao Mercado Pago, você poderá escolher em quantas vezes deseja parcelar sua compra
                                            </div>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {/* Seção de Segurança */}
                            <div style={{
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #e9ecef',
                                borderRadius: '8px',
                                padding: '25px',
                                margin: '25px 0',
                                textAlign: 'center'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: '15px',
                                    gap: '10px'
                                }}>
                                    <i className="fas fa-shield-alt" style={{
                                        fontSize: '2rem',
                                        color: '#28a745'
                                    }}></i>
                                    <h3 style={{
                                        fontSize: '1.4rem',
                                        fontWeight: 'bold',
                                        color: '#000000',
                                        margin: '0'
                                    }}>
                                        Pagamento 100% Seguro
                                    </h3>
                                </div>

                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: '20px',
                                    gap: '15px',
                                    flexWrap: 'wrap'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '10px 15px',
                                        backgroundColor: '#ffffff',
                                        borderRadius: '6px',
                                        border: '1px solid #e0e0e0'
                                    }}>
                                        <i className="fab fa-cc-visa" style={{
                                            fontSize: '1.5rem',
                                            color: '#1434CB'
                                        }}></i>
                                        <i className="fab fa-cc-mastercard" style={{
                                            fontSize: '1.5rem',
                                            color: '#EB001B'
                                        }}></i>
                                        <i className="fab fa-cc-paypal" style={{
                                            fontSize: '1.5rem',
                                            color: '#0070BA'
                                        }}></i>
                                    </div>

                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '10px 15px',
                                        backgroundColor: '#00a650',
                                        borderRadius: '6px',
                                        color: '#ffffff',
                                        fontWeight: 'bold'
                                    }}>
                                        <i className="fas fa-lock" style={{ fontSize: '1.2rem' }}></i>
                                        <span>SSL Criptografado</span>
                                    </div>
                                </div>

                                <div style={{
                                    backgroundColor: '#ffffff',
                                    border: '2px solid #00a650',
                                    borderRadius: '8px',
                                    padding: '20px',
                                    marginBottom: '15px'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '10px',
                                        marginBottom: '10px'
                                    }}>
                                        <img
                                            src="/images/MP_RGB_HANDSHAKE_color_horizontal.svg"
                                            alt="Mercado Pago"
                                            style={{
                                                height: '70px',
                                                objectFit: 'contain'
                                            }}
                                        />
                                        <span style={{
                                            fontSize: '1.1rem',
                                            fontWeight: 'bold',
                                            color: '#00a650'
                                        }}>
                                            Pagamento seguro pelo Mercado Pago
                                        </span>
                                    </div>
                                    <p style={{
                                        fontSize: '0.95rem',
                                        color: '#666',
                                        margin: '0',
                                        lineHeight: '1.4'
                                    }}>
                                        Você será redirecionado para o ambiente seguro do Mercado Pago para finalizar seu pagamento.
                                        Seus dados bancários nunca passam por nossos servidores.
                                    </p>
                                </div>

                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '20px',
                                    flexWrap: 'wrap',
                                    fontSize: '0.9rem',
                                    color: '#666'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                        <i className="fas fa-check-circle" style={{ color: '#28a745' }}></i>
                                        <span>Dados protegidos</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                        <i className="fas fa-check-circle" style={{ color: '#28a745' }}></i>
                                        <span>Pagamento garantido</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                        <i className="fas fa-check-circle" style={{ color: '#28a745' }}></i>
                                        <span>Suporte 24/7</span>
                                    </div>
                                </div>
                            </div>

                            {/* Botão Finalizar */}
                            <div style={{ textAlign: 'center', marginTop: '30px' }}>
                                <button
                                    type="submit"
                                    disabled={submittingOrder}
                                    style={{
                                        padding: '20px 40px',
                                        backgroundColor: submittingOrder ? '#ccc' : '#edf507',
                                        color: '#000000',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontSize: '1.2rem',
                                        fontWeight: 'bold',
                                        cursor: submittingOrder ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.3s ease',
                                        textTransform: 'uppercase',
                                        letterSpacing: '1px',
                                        opacity: submittingOrder ? 0.7 : 1
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!submittingOrder) {
                                            e.target.style.backgroundColor = '#dce406';
                                            e.target.style.transform = 'translateY(-2px)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!submittingOrder) {
                                            e.target.style.backgroundColor = '#edf507';
                                            e.target.style.transform = 'translateY(0)';
                                        }
                                    }}
                                >
                                    {submittingOrder ? 'Processando...' : 'Finalizar Compra'}
                                </button>
                            </div>
                        </div>

                        {/* Resumo do Pedido */}
                        <div className="col-4 col-12-narrower">
                            <div style={{
                                backgroundColor: '#f8f8f8',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                padding: '20px',
                                position: 'sticky',
                                top: '20px'
                            }}>
                                <h3 style={{
                                    fontSize: '1.5rem',
                                    fontWeight: 'bold',
                                    color: '#000000',
                                    margin: '0 0 20px 0',
                                    textAlign: 'center'
                                }}>
                                    Resumo do Pedido
                                </h3>

                                {/* Lista de produtos */}
                                {cartItems.map((item) => (
                                    <div key={item.id} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        marginBottom: '15px',
                                        paddingBottom: '15px',
                                        borderBottom: '1px solid #e0e0e0'
                                    }}>
                                        <img
                                            src={item.image}
                                            alt={item.alt}
                                            style={{
                                                width: '50px',
                                                height: '50px',
                                                objectFit: 'cover',
                                                borderRadius: '4px'
                                            }}
                                        />
                                        <div style={{ flex: '1' }}>
                                            <div style={{
                                                fontSize: '0.9rem',
                                                fontWeight: 'bold',
                                                color: '#000000'
                                            }}>
                                                {item.title}
                                            </div>
                                            <div style={{
                                                fontSize: '0.8rem',
                                                color: '#666'
                                            }}>
                                                Qtd: {item.quantity}
                                            </div>
                                        </div>
                                        <div style={{
                                            fontSize: '0.9rem',
                                            fontWeight: 'bold',
                                            color: '#000000'
                                        }}>
                                            R$ {(item.currentPrice * item.quantity).toFixed(2).replace('.', ',')}
                                        </div>
                                    </div>
                                ))}

                                {/* Totais */}
                                <div style={{ marginTop: '20px' }}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        marginBottom: '10px'
                                    }}>
                                        <span>Subtotal ({getTotalItems()} itens):</span>
                                        <span>R$ {getTotalPrice().toFixed(2).replace('.', ',')}</span>
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        marginBottom: '10px'
                                    }}>
                                        <span>Frete:</span>
                                        <span>
                                            {selectedShipping
                                                ? `R$ ${selectedShipping.price.toFixed(2).replace('.', ',')}`
                                                : 'A calcular'
                                            }
                                        </span>
                                    </div>
                                    <hr style={{ border: '1px solid #e0e0e0', margin: '15px 0' }} />
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        fontSize: '1.2rem',
                                        fontWeight: 'bold'
                                    }}>
                                        <span>Total:</span>
                                        <span>
                                            R$ {selectedShipping
                                                ? (getTotalPrice() + selectedShipping.price).toFixed(2).replace('.', ',')
                                                : getTotalPrice().toFixed(2).replace('.', ',')
                                            }
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </section>
    );
};

export default Checkout;
